<template>
  <div>
    <div
      class="is-flex is-justify-content-space-between mb-3 is-align-items-center assets__header"
    >
      <BaseSelect
        v-if="canViewCountryAndDestinationSelectors"
        v-model="payload.country"
        size="large"
        :list="contractLocations"
        option-value="location_code"
        option-text="location_name"
        placeholder="Select a country"
        :disabled="isLoading"
        class="mb-4"
        label="Country (*)"
        style="width: 30%"
        @input="setDestinationsFromCountry($event)"
      />

      <BaseSelect
        v-if="canViewCountryAndDestinationSelectors"
        v-model="payload.destination"
        size="large"
        :list="destinations"
        option-value="location_code"
        option-text="location_name"
        placeholder="Select a City or Landmark"
        label="City or Landmarks"
        :disabled="isLoading"
        class="mb-4"
        style="width: 30%"
      />

      <BaseInput
        placeholder="Search assets by name..."
        type="search"
        :lazy="true"
        :value="payload.search"
        size="large"
        :style="`${
          canViewCountryAndDestinationSelectors ? 'width: 40%' : 'width: 100%'
        }`"
        @input="payload.search = $event"
      />
      <BaseButton
        action="principal"
        class="mx-1"
        size="large"
        @click="handleLoadData"
      >
        <i slot="icon">
          <IconSearch />
        </i>
      </BaseButton>
    </div>
    <GuideBox
      v-if="!isLoading"
      title="Editing your itinerary"
      description="Let´s add this one"
      :step="42"
      :position-x="-10"
      :position-y="200"
      :show-next-button="false"
    />
    <GuideBox
      title="Editing your itinerary"
      description="Let´s add this one"
      :step="54"
      :position-x="-10"
      :position-y="200"
      :show-next-button="false"
    />
    <div
      v-if="!isAssetsListEmpty"
      id="gallery"
      :data-test-id="constants.DATA_TEST_ID_VALUES.PHOTOS_GALLERY"
    >
      <div
        v-for="(image, index) in data.data"
        :key="index"
        :class="focusedGuideBoxClass('', [42, 54], false, () => index === 0)"
      >
        <expandable-image
          :src="getAssetImage(image)"
          :alt="image.name"
          :title="image.name"
          :close-on-background-click="true"
          class="gallery_img"
        ></expandable-image>
        <div class="is-flex is-align-content-space-between buttons-img">
          <b-checkbox
            class="checkAdd"
            type="is-success"
            :value="
              assetsList
                ? assetsList.findIndex((list) => {
                    return list.id == image.id;
                  }) >= 0
                  ? true
                  : false
                : false
            "
            @input="
              nextStep();
              handleAssetOnClick($event, image);
            "
            >Add</b-checkbox
          >
        </div>
        <p class="description">
          <span>
            Name:<b> {{ image.name | truncate(15, "...") }}</b>
          </span>
          <span v-if="isLocationList()">
            <br />
            Location:<b> {{ image.path }}</b>
          </span>
        </p>
      </div>
    </div>

    <p v-else class="has-text-centered" style="margin-top: 15rem">
      No assets found for this location. Please try again later or upload a new
      asset.
    </p>

    <b-pagination
      v-model="data.currentPage"
      :total="data.total"
      :range-before="1"
      :range-after="2"
      :rounded="false"
      size="is-small"
      :per-page="10"
      order="is-centered"
      class="mt-6"
    >
      <template #default="props">
        <b-pagination-button
          :id="`page${props.page.number}`"
          :page="props.page"
          tag="button"
          @click.native="onPageChange(props.page.number, props)"
        >
          {{ props.page.number }}
        </b-pagination-button>
      </template>

      <template #previous="props">
        <b-pagination-button
          :page="props.page"
          @click.native="onPageChange(props.page.number)"
        >
          <IconArrowLeft class="icon_previous" />
        </b-pagination-button>
      </template>

      <template #next="props">
        <b-pagination-button
          :page="props.page"
          @click.native="onPageChange(props.page.number)"
        >
          <IconArrowRight class="icon_next" />
        </b-pagination-button>
      </template>
    </b-pagination>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import GuideBox from "./GuideBox.vue";
import { CATEGORY_ASSET } from "@/constants";
import { DATA_TEST_ID_VALUES } from "@/constants/test";

export default {
  name: "AssetsList",
  components: { GuideBox },
  props: {
    assetsList: {
      type: Array,
      default: () => [],
    },
    handleAssetOnClick: {
      type: Function,
    },
    toggleLoading: {
      type: Function,
    },
    loadData: {
      type: Function,
    },
    filterDestinations: {
      type: Function,
    },
    category: {
      type: Number,
      default: CATEGORY_ASSET.LOCATION,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      default: "",
    },
    locationPath: {
      type: String,
      default: "",
    },
    imageProcessingId: {
      type: Number,
      default: 0,
    },
    contractLocations: {
      type: Array,
      default: () => [],
    },
    defaultPayload: {
      type: Object,
      default: () => ({
        search: "",
        country: "",
        destination: "",
      }),
    },
  },
  data() {
    return {
      constants: { DATA_TEST_ID_VALUES },
      payload: {
        search: this.defaultPayload.search || "",
        country: this.defaultPayload.country?.location_code || "",
        destination: this.defaultPayload.destination?.location_code || "",
      },
      placeholderImage:
        "https://place-hold.it/1200X725/DDDDDD/fff.jpg?bold&fontsize=50&text=Processing Image",
      destinations: [],
    };
  },
  computed: {
    ...mapGetters({
      list: "assets/list",
      isUserGuideActive: "userguide/getIsUserGuideActive",
      userGuideStep: "userguide/getStep",
    }),
    data() {
      return {
        data: this.list.data,
        total: this.list.meta ? this.list.meta.total : 0,
        currentPage: this.list.meta ? this.list.meta.current_page : 1,
        lastPage: this.list.meta ? this.list.meta.last_page : 1,
        prev: this.list.links ? this.list.links.prev : 1,
        next: this.list.links ? this.list.links.next : 1,
        perPage: this.perPage,
        loading: this.loading,
      };
    },
    isAssetsListEmpty() {
      return this.data?.data?.length === 0;
    },
    hasAssetsSearchDefaultPayload() {
      return (
        this.defaultPayload &&
        (this.defaultPayload.country || this.defaultPayload.destination)
      );
    },
    canViewCountryAndDestinationSelectors() {
      return this.category !== CATEGORY_ASSET.CLIENT;
    },
  },
  mounted() {
    this.initialLoad();
  },
  methods: {
    loadOptionsByDefaultPayload() {
      if (
        this.defaultPayload.country &&
        this.defaultPayload.country.location_code
      ) {
        this.payload.country = this.defaultPayload.country.location_code;

        this.setDestinationsFromCountry(
          this.defaultPayload.country?.location_code
        );

        this.payload.destination =
          this.defaultPayload.destination?.location_code || "";
      }

      this.data.currentPage = 1;
    },
    async setDestinationsFromCountry(event = "") {
      this.destinations = await this.filterDestinations(event);
      if (this.payload.destination) {
        if (
          !this.destinations.some(
            (dest) => dest.location_code === this.payload.destination
          )
        ) {
          this.payload.destination = "";
        }
      }
    },
    isLocationList() {
      return this.category === CATEGORY_ASSET.LOCATION;
    },
    handleLoadData() {
      const payload = {
        page: this.data.currentPage,
        order: "desc",
        search: this.payload.search,
        path: `${this.payload.country}${
          this.payload.destination?.length > 0 ? "/" : ""
        }${this.payload.destination}`,
        category: this.category,
      };
      this.loadData(payload);
    },
    onPageChange(page) {
      this.data.currentPage = page;
      this.handleLoadData();
    },
    focusedGuideBoxClass(
      classDefault,
      step,
      bgWhite = false,
      extraValidator = null
    ) {
      const bgWhiteClass = bgWhite ? "bg-white" : "";
      const checkStep = step.length
        ? step.includes(this.userGuideStep)
        : this.userGuideStep === step;
      const resultExtraValidator = !extraValidator || extraValidator();
      return this.isUserGuideActive && checkStep && resultExtraValidator
        ? `${classDefault} is-guide-step ${bgWhiteClass}`
        : classDefault;
    },
    nextStep() {
      if (this.isUserGuideActive) this.$store.dispatch("userguide/nextStep");
    },
    getAssetImage(image) {
      if (this.imageProcessingId === image.id) {
        return this.placeholderImage;
      }
      return image.thumbnail_1200X725;
    },
    async initialLoad() {
      if (this.hasAssetsSearchDefaultPayload) {
        this.loadOptionsByDefaultPayload();
        await this.handleLoadData();
      }
    },
  },
};
</script>
<style>
.checkAdd {
  height: 45px;
  background: white;
  width: 100%;
  margin: 0 !important;
}

.assets__header {
  gap: 1rem;
}

.assets__header .label {
  font-size: 0.8rem !important;
  text-align: left !important;
  margin-left: 0.5rem !important;
}

@media screen and (max-width: 480px) {
  #gallery {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
